import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CognitoUser, CognitoUserPool, AuthenticationDetails, CognitoUserSession, ChallengeName } from 'amazon-cognito-identity-js';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable, of, from, switchMap, catchError, throwError, map, firstValueFrom } from 'rxjs';
import { HttpHeaders, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { User } from 'shared_models';
import { ApiResponse } from 'shared_models';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { LoggerService } from './logger.service';
import { Storage } from '@ionic/storage';

/** Ionic Storage IndexedDB name — preserved on logout so offline pull-test data survives. */
const IONIC_STORAGE_DB_NAME = '_ionicstorage';
const LAST_USERNAME_STORAGE_KEY = 'lastUsername';

interface ChallengeParameters {
  userAttributes: { [key: string]: string };
  requiredAttributes: string[];
}

interface CognitoUserWithChallenge extends CognitoUser {
  challengeParameters?: ChallengeParameters;
  challengeName?: ChallengeName;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private userPool: CognitoUserPool;
  private currentUser: User | null = null;
  private currentCognitoUser: CognitoUserWithChallenge | null = null;
  /**
   * Session from the most recent successful Cognito auth.
   * Used so login `onSuccess` can call `/users/me` without re-entering `getSession`
   * (which can hang indefinitely inside amazon-cognito-identity-js).
   */
  private lastKnownSession: CognitoUserSession | null = null;
  private sessionCheckInterval: any;
  private readonly SESSION_CHECK_INTERVAL = 60000; // Check every minute
  private authState = new BehaviorSubject<boolean>(false);
  private lastTokenRefreshTime: number = 0;
  protected newPasswordRequiredUser: CognitoUserWithChallenge | null = null;
  private readonly tokenRefreshInterval = 50 * 60 * 1000; // 50 minutes
  private readonly FIRST_TIME_LOGIN_TIMEOUT = 30 * 60 * 1000; // 30 minutes timeout
  private firstTimeLoginStartTime: number = 0;
  /** When true, session expiry checks are suppressed during pull-test offline mode. */
  private offlineModeSuppressExpiry = false;

  constructor(
    private router: Router,
    private http: HttpClient,
    private logger: LoggerService
  ) {
    this.logger.log('Initializing AuthService with config:', {
      UserPoolId: environment.cognitoConfig.UserPoolId,
      ClientId: environment.cognitoConfig.ClientId,
      Region: environment.cognitoConfig.Region
    });
    
    this.userPool = new CognitoUserPool({
      UserPoolId: environment.cognitoConfig.UserPoolId,
      ClientId: environment.cognitoConfig.ClientId
    });

    // Start session monitoring
    this.startSessionMonitoring();
    this.setupTokenRefresh();
  }

  private startSessionMonitoring() {
    // Clear any existing interval
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
    }

    // Set up periodic session check
    this.sessionCheckInterval = setInterval(async () => {
      await this.checkSession();
    }, this.SESSION_CHECK_INTERVAL);
  }

  private pauseSessionMonitoring() {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }
  }

  private resumeSessionMonitoring() {
    if (!this.sessionCheckInterval) {
      this.startSessionMonitoring();
    }
  }

  private clearFirstTimeLoginState() {
    this.firstTimeLoginStartTime = 0;
    this.newPasswordRequiredUser = null;
    sessionStorage.removeItem('NEW_PASSWORD_REQUIRED');
    sessionStorage.removeItem('USER_ATTRIBUTES');
  }

  private async checkSession() {
    try {
      if (this.offlineModeSuppressExpiry) {
        return;
      }

      // Don't check session if user is in new password required flow
      const isNewPasswordRequired = sessionStorage.getItem('NEW_PASSWORD_REQUIRED') === 'true';
      if (isNewPasswordRequired) {
        this.logger.log('Session check skipped - user is in new password required flow');
        
        // Check if first-time login has timed out
        if (this.firstTimeLoginStartTime > 0) {
          const timeElapsed = Date.now() - this.firstTimeLoginStartTime;
          if (timeElapsed > this.FIRST_TIME_LOGIN_TIMEOUT) {
            this.logger.log('First-time login timed out, clearing state');
            this.clearFirstTimeLoginState();
            this.handleSessionExpired();
          }
        }
        return;
      }

      // Don't check session if we don't have a current user
      if (!this.currentCognitoUser) {
        this.logger.log('Session check skipped - no current Cognito user');
        return;
      }

      const session = await this.getCurrentSession();
      if (!session) {
        this.handleSessionExpired();
        return;
      }

      // Check if the access token is about to expire (within 5 minutes)
      const expirationTime = session.getAccessToken().getExpiration() * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiration = expirationTime - currentTime;

      if (timeUntilExpiration <= 300000) { // 5 minutes in milliseconds
        this.logger.log('Token expiring soon, refreshing session...');
        await this.refreshSession();
      }
    } catch (error) {
      this.logger.error('Error checking session:', error);
      // Don't immediately logout on session check errors during first-time login
      const isNewPasswordRequired = sessionStorage.getItem('NEW_PASSWORD_REQUIRED') === 'true';
      if (!isNewPasswordRequired) {
        this.handleSessionExpired();
      }
    }
  }

  private async refreshSession(): Promise<void> {
    const cognitoUser = this.resolveCognitoUser();
    if (!cognitoUser) {
      throw new Error('No Cognito user session found');
    }

    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err) {
          this.logger.error('Error refreshing session:', err);
          if (!this.offlineModeSuppressExpiry) {
            this.handleSessionExpired();
          }
          reject(err);
          return;
        }

        if (!session) {
          reject(new Error('No session'));
          return;
        }

        if (session.isValid()) {
          this.currentCognitoUser = cognitoUser;
          this.lastTokenRefreshTime = Date.now();
          resolve();
          return;
        }

        const refreshToken = session.getRefreshToken();
        if (!refreshToken) {
          reject(new Error('No refresh token'));
          return;
        }

        cognitoUser.refreshSession(refreshToken, (refreshErr, newSession) => {
          if (refreshErr || !newSession) {
            this.logger.error('Error refreshing Cognito session:', refreshErr);
            if (!this.offlineModeSuppressExpiry) {
              this.handleSessionExpired();
            }
            reject(refreshErr ?? new Error('Session refresh failed'));
            return;
          }

          this.currentCognitoUser = cognitoUser;
          this.lastTokenRefreshTime = Date.now();
          resolve();
        });
      });
    });
  }

  /** Restores in-memory Cognito user from the pool after reload (tokens live in browser storage). */
  private resolveCognitoUser(): CognitoUserWithChallenge | null {
    if (this.currentCognitoUser) {
      return this.currentCognitoUser;
    }
    const fromPool = this.userPool.getCurrentUser();
    if (!fromPool) {
      return null;
    }
    this.currentCognitoUser = fromPool as CognitoUserWithChallenge;
    return this.currentCognitoUser;
  }

  private handleSessionExpired() {
    if (this.offlineModeSuppressExpiry) {
      this.logger.log('Session expired suppressed — pull-test offline mode active');
      return;
    }
    this.logger.log('Session expired, logging out user');
    this.lastKnownSession = null;
    this.authState.next(false);
    this.logout();
  }

  /** Pause Cognito session checks while pull-test offline mode is active. */
  pauseForOfflineMode(): void {
    this.offlineModeSuppressExpiry = true;
    this.pauseSessionMonitoring();
  }

  /** Resume Cognito session checks after pull-test offline mode ends. */
  resumeAfterOfflineMode(): void {
    this.offlineModeSuppressExpiry = false;
    this.resumeSessionMonitoring();
  }

  /** Attempt token refresh before uploading offline changes. Returns false if refresh fails. */
  async tryRefreshSession(): Promise<boolean> {
    try {
      await this.refreshSession();
      if (!this.currentUser) {
        try {
          const user = await firstValueFrom(this.getCurrentUser());
          this.currentUser = user;
        } catch (e) {
          this.logger.warn('Session refreshed but user profile fetch failed', e);
        }
      }
      return true;
    } catch (e) {
      this.logger.warn('tryRefreshSession failed', e);
      return false;
    }
  }

  /**
   * Username/email hint for in-modal re-auth when leaving offline mode (cached profile, Cognito, or last login).
   */
  async getSuggestedReauthUsername(): Promise<string | null> {
    if (this.currentUser?.email?.trim()) {
      return this.currentUser.email.trim();
    }
    const cognitoUser = this.resolveCognitoUser();
    const cognitoUsername = cognitoUser?.getUsername?.();
    if (typeof cognitoUsername === 'string' && cognitoUsername.trim()) {
      return cognitoUsername.trim();
    }
    return this.readLastUsernameFromStorage();
  }

  /**
   * Re-authenticate with password without leaving the current screen (used when exiting offline mode).
   * Does not navigate away on success; rejects if a new-password challenge is required.
   */
  async reauthenticateWithPassword(username: string, password: string): Promise<void> {
    const trimmedUser = username.trim();
    const trimmedPass = password.trim();
    if (!trimmedUser || !trimmedPass) {
      throw new Error('Enter your email and password.');
    }

    await new Promise<void>((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: trimmedUser,
        Password: trimmedPass,
      });
      const cognitoUser = new CognitoUser({
        Username: trimmedUser,
        Pool: this.userPool,
      });
      cognitoUser.setAuthenticationFlowType('USER_PASSWORD_AUTH');

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: async () => {
          this.currentCognitoUser = cognitoUser as CognitoUserWithChallenge;
          this.authState.next(true);
          this.lastTokenRefreshTime = Date.now();
          try {
            const user = await firstValueFrom(this.getCurrentUser());
            this.currentUser = user;
            resolve();
          } catch (e) {
            reject(e);
          }
        },
        onFailure: (err: Error) => {
          this.authState.next(false);
          reject(err);
        },
        newPasswordRequired: () => {
          reject(
            new Error(
              'Your account requires a password change. Sign in from the login screen to update your password, then return here to upload your offline changes.'
            )
          );
        },
      });
    });
  }

  private async readLastUsernameFromStorage(): Promise<string | null> {
    try {
      const storage = new Storage();
      await storage.create();
      const value = await storage.get(LAST_USERNAME_STORAGE_KEY);
      return typeof value === 'string' && value.trim() ? value.trim() : null;
    } catch {
      return null;
    }
  }

  isOfflineModeSuppressingExpiry(): boolean {
    return this.offlineModeSuppressExpiry;
  }

  login(username: string, password: string): Promise<boolean> {
    this.logger.log('🔐 AuthService - login: Attempting login for user:', username);
    
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: username,
        Password: password
      });

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: this.userPool
      });

      // Set the authentication flow type to USER_PASSWORD_AUTH
      cognitoUser.setAuthenticationFlowType('USER_PASSWORD_AUTH');

      const authCallbacks = {
        onSuccess: async (session: CognitoUserSession) => {
          this.logger.log('=== Authentication Success ===');
          this.currentCognitoUser = cognitoUser;
          this.lastKnownSession = session;
          this.authState.next(true);
          this.lastTokenRefreshTime = Date.now();
          
          try {
            const user = await firstValueFrom(this.getCurrentUser());
            this.currentUser = user || null;
            this.logger.log('User fetched successfully:', this.currentUser);
            resolve(true);
          } catch (error) {
            this.logger.error('Error fetching user after login:', error);
            reject(error);
          }
        },
        onFailure: (err: Error) => {
          this.logger.log('=== Authentication Failure ===');
          this.logger.error('Error details:', err);
          this.logger.error('Error message:', err.message);
          this.logger.error('Error stack:', err.stack);
          
          // Ensure we're not authenticated on failure
          this.authState.next(false);
          this.currentCognitoUser = null;
          this.currentUser = null;
          
          reject(err);
        },
        newPasswordRequired: (userAttributes: any, requiredAttributes: any) => {
          this.logger.log('=== New Password Required ===');
          this.logger.log('User attributes:', userAttributes);
          this.logger.log('Required attributes:', requiredAttributes);
          
          // Pause session monitoring during first-time login
          this.pauseSessionMonitoring();
          
          // Set timeout for first-time login
          this.firstTimeLoginStartTime = Date.now();
          
          // Store the user requiring password change
          this.newPasswordRequiredUser = cognitoUser;
          this.logger.log('Set newPasswordRequiredUser:', this.newPasswordRequiredUser);
          
          // Store the user attributes for later use
          sessionStorage.setItem('NEW_PASSWORD_REQUIRED', 'true');
          sessionStorage.setItem('USER_ATTRIBUTES', JSON.stringify(userAttributes));
          
          // Ensure we're not authenticated yet
          this.authState.next(false);
          this.currentCognitoUser = null;
          this.currentUser = null;
          
          // Use setTimeout to ensure navigation happens after current execution cycle
          setTimeout(() => {
            this.logger.log('Attempting navigation to first-time-password');
            this.router.navigate(['/first-time-password'], { 
              replaceUrl: true,
              skipLocationChange: true 
            }).then(success => {
              this.logger.log('Navigation result:', success);
              if (!success) {
                this.logger.error('Navigation failed, trying alternative approach');
                // Try alternative navigation if the first attempt fails
                this.router.navigateByUrl('/first-time-password', { 
                  replaceUrl: true,
                  skipLocationChange: true 
                });
              }
            }).catch(error => {
              this.logger.error('Navigation error:', error);
            });
          }, 100);
          
          // Throw a specific error that the login page can catch
          reject(new Error('NEW_PASSWORD_REQUIRED'));
        },
        mfaRequired: (challengeName: string, challengeParameters: any) => {
          this.logger.log('=== MFA Required ===');
          this.logger.log('Challenge name:', challengeName);
          this.logger.log('Challenge parameters:', challengeParameters);
          
          // Ensure we're not authenticated
          this.authState.next(false);
          this.currentCognitoUser = null;
          this.currentUser = null;
          
          reject(new Error('MFA required'));
        },
        mfaSetup: (challengeName: string, challengeParameters: any) => {
          this.logger.log('=== MFA Setup Required ===');
          this.logger.log('Challenge name:', challengeName);
          this.logger.log('Challenge parameters:', challengeParameters);
          
          // Ensure we're not authenticated
          this.authState.next(false);
          this.currentCognitoUser = null;
          this.currentUser = null;
          
          reject(new Error('MFA setup required'));
        },
        totpRequired: (challengeName: string, challengeParameters: any) => {
          this.logger.log('=== TOTP Required ===');
          this.logger.log('Challenge name:', challengeName);
          this.logger.log('Challenge parameters:', challengeParameters);
          
          // Ensure we're not authenticated
          this.authState.next(false);
          this.currentCognitoUser = null;
          this.currentUser = null;
          
          reject(new Error('TOTP required'));
        }
      };

      cognitoUser.authenticateUser(authenticationDetails, authCallbacks);
    });
  }

  async completeNewPasswordChallenge(newPassword: string): Promise<boolean> {
    if (!this.newPasswordRequiredUser) {
      this.logger.log('No user requiring new password');
      throw new Error('No user requiring new password');
    }

    const user = this.newPasswordRequiredUser;
    return new Promise((resolve, reject) => {
      user.completeNewPasswordChallenge(
        newPassword,
        {},
        {
          onSuccess: async (session: CognitoUserSession) => {
            this.logger.log('=== New Password Challenge Completed ===');
            this.currentCognitoUser = user;
            this.lastKnownSession = session;
            this.authState.next(true);
            this.lastTokenRefreshTime = Date.now();
            this.newPasswordRequiredUser = null;
            
            // Clear the session storage
            sessionStorage.removeItem('NEW_PASSWORD_REQUIRED');
            sessionStorage.removeItem('USER_ATTRIBUTES');
            
            // Resume session monitoring after successful password change
            this.resumeSessionMonitoring();
            
            // Fetch the user data
            try {
              const userData = await firstValueFrom(this.getCurrentUser());
              this.currentUser = userData || null;
              this.logger.log('User data fetched after password change:', this.currentUser);
              resolve(true);
            } catch (error) {
              this.logger.error('Error fetching user after password change:', error);
              reject(error);
            }
          },
          onFailure: (err: Error) => {
            this.logger.error('Failed to complete new password challenge:', err);
            reject(err);
          }
        }
      );
    });
  }

  async logout(): Promise<void> {
    // Preserve lastUsername from Ionic Storage
    let lastUsername: string | null = null;
    try {
      const storage = new (window as any).Storage();
      storage.create().then(() => {
        storage.get('lastUsername').then((value: string | null) => {
          lastUsername = value;
        });
      });
    } catch (e) { /* ignore */ }
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
    }
    if (this.currentUser) {
      this.logger.log('Logging out user');
      this.currentCognitoUser?.signOut();
      this.currentUser = null;
    }
    this.authState.next(false);
    this.lastKnownSession = null;
    // Clear session/local storage (Cognito tokens). Do not wipe Ionic Storage IndexedDB — offline pull-test data lives there.
    sessionStorage.clear();
    localStorage.clear();
    if (window && window.indexedDB && indexedDB.databases) {
      indexedDB.databases().then(dbs => {
        for (const db of dbs) {
          if (!db.name || db.name === IONIC_STORAGE_DB_NAME) {
            continue;
          }
          indexedDB.deleteDatabase(db.name);
        }
      });
    }
    // Restore lastUsername after clearing storage
    if (lastUsername) {
      try {
        const storage = new (window as any).Storage();
        storage.create().then(() => {
          storage.set('lastUsername', lastUsername);
        });
      } catch (e) { /* ignore */ }
    }

    // Allow rotation on the login screen (portrait and landscape)
    try {
      await ScreenOrientation.unlock();
      this.logger.log('Screen orientation unlocked for login screen');
    } catch (error) {
      this.logger.error('Error unlocking screen orientation:', error);
    }

    this.router.navigate(['/login']);
  }

  async isAuthenticated(): Promise<boolean> {
    // Check if we're in a new password required flow
    const isNewPasswordRequired = sessionStorage.getItem('NEW_PASSWORD_REQUIRED') === 'true';
    if (isNewPasswordRequired) {
      this.logger.log('isAuthenticated: New password required flow detected, returning true');
      return true;
    }
    
    try {
      const session = await this.getCurrentSession();
      const isValid = session !== null && session.isValid();
      
      // Update the auth state subject
      this.authState.next(isValid);
      
      if (isValid) {
        // If we have a valid session but no current user, try to fetch it
        // Don't fail authentication if the API call fails - the user can still be authenticated
        if (!this.currentUser) {
          try {
            const user = await firstValueFrom(this.getCurrentUser());
            this.currentUser = user || null;
          } catch (error) {
            this.logger.error('Error fetching current user:', error);
            // Don't return false here - the user is still authenticated via Cognito
            // The user data will be fetched when needed
          }
        }
        return true;
      }
      
      return false;
    } catch (error) {
      this.logger.error('Error checking authentication:', error);
      this.authState.next(false);
      return false;
    }
  }

  getAuthState() {
    return this.authState.asObservable();
  }

  getCurrentUser(): Observable<User> {
    if(this.currentUser) {
      return of(this.currentUser);
    }
    return from(this.getHeaders()).pipe(
      switchMap(headers => {
        return this.http.get<ApiResponse<User>>(`${environment.apiUrl}/users/me`, { headers }).pipe(
          map(response => response.data)
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Updates the cached user snapshot after PUT /users/me returns so callers using
   * `getCurrentUser()` see fresh profile fields (including Cognito-synced avatar color).
   */
  setCurrentUserSnapshot(user: User | null): void {
    this.currentUser = user;
  }

  /** Arrow so `catchError(this.handleError)` keeps `this`. */
  private handleError = (error: HttpErrorResponse) => {
    this.logger.error('API Error:', error);
    return throwError(() => error);
  };

  getCurrentCognitoUser(): CognitoUserWithChallenge | null {
    // If we have a stored challenge state, return the user requiring password change
    if (sessionStorage.getItem('NEW_PASSWORD_REQUIRED') === 'true' && this.newPasswordRequiredUser) {
      return this.newPasswordRequiredUser;
    }
    return this.currentCognitoUser;
  }

  async getCurrentSession(): Promise<CognitoUserSession | null> {
    if (this.lastKnownSession?.isValid()) {
      return this.lastKnownSession;
    }

    this.logger.debug('🔐 Getting Current Session: Step 1: Getting current Cognito user');
    
    return new Promise((resolve, reject) => {
      const cognitoUser = this.userPool.getCurrentUser();
      if (!cognitoUser) {
        this.logger.debug('Step 2: No current Cognito user found');
        resolve(null);
        return;
      }

      this.logger.debug('Step 2: Getting session for current user');
      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err) {
          this.logger.error('Step 3: Error getting session:', err);
          reject(err);
          return;
        }

        if (!session) {
          this.logger.debug('Step 3: No session found');
          resolve(null);
          return;
        }

        this.logger.debug('Step 3: Session found, checking validity');
        const isValid = session.isValid();
        this.logger.debug('Session valid:', isValid);
        
        if (!isValid) {
          this.logger.debug('Step 4: Session is invalid');
          resolve(null);
          return;
        }

        this.logger.debug('Step 4: Valid session found');
        this.lastKnownSession = session;
        resolve(session);
      });
    });
  }

  async getHeaders(): Promise<HttpHeaders> {
    this.logger.debug('🔑 Getting API Headers: Step 1: Attempting to get current session');
    
    // Check if we're in a new password required flow
    const isNewPasswordRequired = sessionStorage.getItem('NEW_PASSWORD_REQUIRED') === 'true';
    if (isNewPasswordRequired) {
      this.logger.debug('Step 1.5: New password required flow detected, skipping session check');
      return new HttpHeaders({
        'Content-Type': 'application/json'
      });
    }
    
    let token = '';
    try {
      const session = await this.getCurrentSession();
      
      if (!session) {
        this.logger.warn('Step 2: No valid session found');
        return new HttpHeaders({
          'Content-Type': 'application/json'
        });
      }
      
      this.logger.debug('Step 2: Session found, extracting token');
      token = session.getIdToken().getJwtToken();
      this.logger.debug('Token (first 20 chars):', token.substring(0, 20) + '...');
      
      // Debug: Log current user info
      this.logger.debug('🔍 DEBUG: Current user in auth service:', {
        currentUser: this.currentUser,
        role: this.currentUser?.role,
        userID: this.currentUser?.userID,
        email: this.currentUser?.email
      });
      
      this.logger.debug('Step 3: Creating headers with token');
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      });
      
      this.logger.debug('Step 4: Headers created successfully');
      return headers;
    } catch (error) {
      this.logger.error('Step 2: Error getting headers:', error);
      if (error instanceof Error) {
        this.logger.error('Error message:', error.message);
      }
      return new HttpHeaders({
        'Content-Type': 'application/json'
      });
    }
  }
  
  changePassword(oldPassword: string, newPassword: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!this.currentCognitoUser) {
        reject(new Error('No current user'));
        return;
      }
      this.currentCognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
        if (err) {
          this.logger.error('Change password error:', err);
          reject(err);
        } else {
          this.logger.log('Password changed successfully:', result);
          resolve(true);
        }
      });
    });
  }

  /**
   * Initiates the forgot password flow for a user (sends a code to their email)
   */
  forgotPassword(username: string): Promise<'SUCCESS' | 'ERROR'> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: this.userPool
      });
      cognitoUser.forgotPassword({
        onSuccess: () => {
          resolve('SUCCESS');
        },
        onFailure: (err) => {
          reject(err);
        },
        inputVerificationCode: () => {
          // This will be handled in the UI
          resolve('SUCCESS');
        }
      });
    });
  }

  /**
   * Confirms the new password using the code sent to the user's email
   */
  confirmForgotPassword(username: string, code: string, newPassword: string): Promise<'SUCCESS' | 'ERROR'> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: this.userPool
      });
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          resolve('SUCCESS');
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  }

  isNewPasswordRequired(): boolean {
    this.logger.debug('🔐 AuthService - isNewPasswordRequired: newPasswordRequiredUser:', this.newPasswordRequiredUser);
    const result = this.newPasswordRequiredUser !== null;
    this.logger.debug('Result:', result);
    return result;
  }

  private setupTokenRefresh() {
    setInterval(async () => {
      if (this.offlineModeSuppressExpiry) {
        return;
      }

      // Check if we're in a new password required flow
      const isNewPasswordRequired = sessionStorage.getItem('NEW_PASSWORD_REQUIRED') === 'true';
      if (isNewPasswordRequired) {
        this.logger.debug('Token refresh: New password required flow detected, skipping session check');
        return;
      }
      
      const timeSinceLastRefresh = Date.now() - this.lastTokenRefreshTime;
      if (timeSinceLastRefresh >= this.tokenRefreshInterval) {
        const session = await this.getCurrentSession();
        if (!session) {
          this.handleSessionExpired();
        } else {
          this.lastTokenRefreshTime = Date.now();
        }
      }
    }, 60000); // Check every minute
  }
}
